'use client'

import React, { useRef } from "react";
import { LanguageType, LanguageDirection } from "@/lib/types";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import Image from "next/image";
import Subheading from "@/components/ui/Subheading/Subheading";
import { useIsLg, useIsMidLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);


interface LPAboutSectionProps {
    language?: LanguageType;
    headlineText: string;
    subheadingText: string;
    paragraphText: string;
    paragraphText2: string;
    imageSrc: string;
    imageAlt: string;
    id?: string;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
    english: "ltr",
    arabic: "rtl"
};

const LPAboutSection: React.FC<LPAboutSectionProps> = ({
    language = "english",
    headlineText,
    subheadingText,
    paragraphText,
    paragraphText2,
    imageSrc,
    imageAlt,
    id,
}) => {
    const isLg = useIsLg();
    const isMidLg = useIsMidLg();
    const sectionRef = useRef<HTMLElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const image = imageRef.current;
        const container = imageContainerRef.current;

        if (!image || !container || !isLg) return;

        gsap.fromTo(image, {
            yPercent: -10
        }, {
            yPercent: 10,
            ease: "none",
            force3D: true,
            scrollTrigger: {
                trigger: container,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
                invalidateOnRefresh: true,
                id: "lp-about-section-parallax"
            },
            onEnter: () => {
                image.style.willChange = "transform";
            },
            onLeave: () => {
                image.style.willChange = "auto";
            },
            onEnterBack: () => {
                image.style.willChange = "transform";
            },
            onLeaveBack: () => {
                image.style.willChange = "auto";
            }
        });
    }, { dependencies: [isLg], revertOnUpdate: true });

    return (
        <section
            ref={sectionRef}
            id={id}
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className="w-full flex justify-center px-[0.75rem] tb:px-[3.5rem]"
        >
            <div
                style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                className={`w-full max-w-[113rem] flex flex-col midlg:flex-row gap-[1rem] items-start`}>
                {/* Content Panel - Left on desktop, top on mobile */}
                <div
                    style={{ direction: "ltr" }}
                    ref={contentRef}
                    // midlg:w-[30.6875rem]
                    className={`
                        w-full
                        midlg:max-w-[43.3125rem]
                        flex-grow flex flex-col
                        gap-[1.5rem] midlg:gap-[3.5rem]
                        px-[0.5rem]
                        midlg:p-[3.5rem] midlg:pl-[6.3125rem] midlg:h-[49.75rem]
                        ${language === 'arabic' ? 'midlg:items-end' : 'midlg:items-center'}  midlg:justify-between
                    `}
                >
<div
                    className={`flex flex-col gap-[1.25rem] midlg:gap-[1.5rem] w-full ${language === 'arabic' ? 'items-end' : 'items-start'}`}>
                        {subheadingText && <div>
                            <Subheading
                                size={isMidLg ? 24 : 20}
                                subheading={subheadingText}
                                alignment={language === 'arabic' ? 'right' : 'left'}
                                textColor="text-text-quaternary" />
                        </div>}

                        <h2
                            // style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                            className="
						        text-text-primary text-52-to-30--neue-june
					        "
                        >
                            {headlineText}
                        </h2>
                    </div>

                    <div className={`
                        flex flex-col gap-[1.5rem] w-full midlg:h-full
                        ${language === 'arabic' ? 'midlg:items-end text-right' : 'midlg:items-start text-left'}
                        midlg:justify-end
                        `}>
                        <p className="text-18--alexandria text-text-tertiary w-full midlg:max-w-[30.6875rem] ">
                            {paragraphText}
                        </p>
                        <p className="text-18--alexandria text-text-tertiary w-full midlg:max-w-[30.6875rem]">
                            {paragraphText2}
                        </p>

                    </div>
                </div>

                {/* Image Panel - Right on desktop, bottom on mobile */}
                <div className="w-full midlg:w-[37rem] flex-grow">
                    <div ref={imageContainerRef} className="relative w-full aspect-[5/3]  midlg:h-[49.75rem] overflow-hidden rounded-[1.5rem] lg:rounded-[2rem]">
                        <Image
                            ref={imageRef}
                            src={imageSrc}
                            alt={imageAlt}
                            fill
                            sizes="(min-width: 992px) 40vw, 100vw"
                            loading="lazy"
                            className="object-cover"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LPAboutSection;
